/**
 * NewsCountdownSection - Pure Presentation Component
 * 
 * ✓ STATELESS - Receives countdown data from parent
 * ✓ REUSABLE - Works in Homepage, CMS, Approval Preview
 * ✓ PURE - Only receives data props and renders
 * 
 * Props:
 *   data: {
 *     kicker: string,
 *     title: string,
 *     intro: string,
 *     latestNewsLabel: string,
 *     newsItems: array,
 *     countdownLabel: string,
 *     countdownTitle: string,
 *     countdownDate: string,
 *     countdownDescription: string,
 *     countdownDays/Hours/Minutes/Seconds: string (labels),
 *   }
 *   countdown: {
 *     days: number,
 *     hours: number,
 *     minutes: number,
 *     seconds: number,
 *     isReached: boolean,
 *   }
 *   images: array (for news thumbnails)
 *   currentLanguage: string ('en' or 'id')
 *   editableMode: boolean (optional)
 *   selectedSection: string (optional)
 *   onEditSection: function (optional)
 *   onOpenItemManager: function (optional)
 */
function NewsCountdownSection({
  data,
  countdown = {},
  images = [],
  currentLanguage = 'en',
  editableMode = false,
  selectedSection = null,
  onEditSection = null,
  onOpenItemManager = null,
  onNavigate = null,
}) {
  if (!data) return null;

  const {
    kicker,
    title,
    intro,
    latestNewsLabel,
    newsItems = [],
    countdownLabel,
    countdownTitle,
    countdownDate,
    countdownDescription,
    countdownDays: countdownDaysLabel,
    countdownHours: countdownHoursLabel,
    countdownMinutes: countdownMinutesLabel,
    countdownSeconds: countdownSecondsLabel,
    countdownComplete,
  } = data;

  const {
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
    isReached = false,
  } = countdown;

  return (
    <section className="news-countdown-section" aria-label="News and countdown">
      <div className="news-countdown-shell">
        <div className="news-countdown-header">
          <p className="news-countdown-kicker">{kicker}</p>
          <h2 className="news-countdown-title">{title}</h2>
          <p className="news-countdown-intro">{intro}</p>
        </div>

        <div className="news-countdown-grid">
          <article className="news-panel-card">
            <div className="news-panel-header">
              <h3 className="news-panel-title">{latestNewsLabel}</h3>
              <button 
                className="news-see-all-btn"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate('news-and-updates');
                  } else {
                    window.location.hash = '#news-and-updates';
                  }
                }}
              >
                <span>{currentLanguage === 'en' ? 'See All' : 'Lihat Semua'}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
            <div className="news-list">
              {newsItems.map((item, index) => {
                const targetSlug = item.link || (item.id ? `news-blog-${item.id}` : `news/${index + 1}`);
                const handleItemClick = () => {
                  if (onNavigate) {
                    onNavigate(targetSlug);
                  } else {
                    window.location.hash = `#${targetSlug}`;
                  }
                };

                return (
                  <article 
                    key={item.title || index} 
                    className="news-item-card"
                    onClick={handleItemClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick();
                      }
                    }}
                  >
                    <div className="news-item-image-wrap">
                      {((images && images.length > 0 && images[index]) || item.image) ? (
                        <img
                          src={(images && images[index]) || item.image}
                          alt={item.title}
                          className="news-item-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="news-item-image-fallback">
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="news-item-content">
                      <div className="news-item-meta">
                        <p className="news-item-date">{item.date}</p>
                        {item.category && (
                          <span className="news-item-category">
                            {item.category}
                          </span>
                        )}
                      </div>
                      <h4>{item.title}</h4>
                      <p>{item.summary}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </article>

          <article className="countdown-panel-card">
            <p className="countdown-label">{countdownLabel}</p>
            <h3>{countdownTitle}</h3>
            <div className="countdown-date-chip">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span>{countdownDate}</span>
            </div>
            <p className="countdown-description">{countdownDescription}</p>

            {isReached ? (
              <p className="countdown-complete">{countdownComplete}</p>
            ) : (
              <div className="countdown-grid" aria-label="Opening day countdown values">
                <div className="countdown-value-box">
                  <strong>{String(days).padStart(2, '0')}</strong>
                  <span>{countdownDaysLabel}</span>
                </div>
                <div className="countdown-value-box">
                  <strong>{String(hours).padStart(2, '0')}</strong>
                  <span>{countdownHoursLabel}</span>
                </div>
                <div className="countdown-value-box">
                  <strong>{String(minutes).padStart(2, '0')}</strong>
                  <span>{countdownMinutesLabel}</span>
                </div>
                <div className="countdown-value-box">
                  <strong>{String(seconds).padStart(2, '0')}</strong>
                  <span>{countdownSecondsLabel}</span>
                </div>
              </div>
            )}
          </article>
        </div>

        {editableMode && selectedSection === 'news' && onEditSection && (
          <div className="cms-section-overlay cms-section-overlay-bottom" onClick={(e) => e.stopPropagation()}>
            <div className="cms-section-overlay-badge">News & Countdown</div>
            <div className="cms-section-overlay-buttons">
              <button 
                className="cms-overlay-btn cms-overlay-btn-edit"
                onClick={() => onEditSection('news')}
              >
                Edit Header
              </button>
              <button 
                className="cms-overlay-btn cms-overlay-btn-items"
                onClick={() => onOpenItemManager?.('news')}
              >
                Manage Items
              </button>
            </div>
          </div>
        )}

        {editableMode && selectedSection === 'countdown' && onEditSection && (
          <div className="cms-section-overlay cms-section-overlay-bottom" onClick={(e) => e.stopPropagation()}>
            <div className="cms-section-overlay-badge">Countdown</div>
            <div className="cms-section-overlay-buttons">
              <button 
                className="cms-overlay-btn cms-overlay-btn-edit"
                onClick={() => onEditSection('countdown')}
              >
                Edit Section
              </button>
              <button 
                className="cms-overlay-btn cms-overlay-btn-preview"
                title="Preview draft changes"
              >
                Preview Draft
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default NewsCountdownSection;
